import { useCallback, useEffect, useMemo, useState } from 'react';
import { GridColDef, GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { useIntl } from 'react-intl';
import { useProject } from 'store/project';
import { DocumentationViewItem } from 'store/documentation/model/types';
import { useDocumentationStore } from 'store/documentation';
import { DocumentationColumnsFactory } from '../columns';
import { getInitialStateFromStorage } from 'components/common/dataGrid/utils';
import { DataGridInstanceName } from 'services/storage/keys';
import {
  DocumentState,
  GridColumnFieldDocumentation,
} from 'components/common/dataGrid/columnsFactory/types';
import { useValueGetters } from './useValueGetters';
import { DEFAULT_SORT_MODEL, isSearchActive, resolveSortModel } from 'store/documentation/slices/filters/model';

export interface DataGridState {
  rows: DocumentationViewItem[];
  columns: GridColDef<DocumentationViewItem>[];
  filterModel: GridFilterModel;
  setFilterModel: (model: GridFilterModel) => void;
  sortModel: GridSortModel;
  setSortModel: (model: GridSortModel) => void;
  searchPhrase: string;
  setSearchPhrase: (phrase: string) => void;
  isLoading: boolean;
  isLoadingDirectories: boolean;
  isLoadingDocumentations: boolean;
  isLoadingSearch: boolean;
  isFiltering: boolean;
  currentDirectory: string;
  navigateToDirectory: (directoryId: string, directoryName?: string) => Promise<void>;
  clearFiltersAndNavigate: (state: DocumentState) => void;
}

export function useDataGridState(): DataGridState {
  const intl = useIntl();
  const { organizationId, timezone } = useProject();

  const filteredRows = useDocumentationStore.use.filteredRows();
  const removedRowIds = useDocumentationStore.use.removedRowIds();
  const isLoadingCurrentDirDirectories = useDocumentationStore.use.isLoadingCurrentDirDirectories();
  const isLoadingCurrentDirDocumentations = useDocumentationStore.use.isLoadingCurrentDirDocumentations();
  const isLoadingSearch = useDocumentationStore.use.isLoadingSearch();
  const isFiltering = useDocumentationStore.use.isFiltering();
  const searchPhrase = useDocumentationStore.use.searchPhrase();
  const searchedDocumentations = useDocumentationStore.use.searchedDocumentations();
  const searchedDirectories = useDocumentationStore.use.searchedDirectories();
  const documentState = useDocumentationStore.use.documentState();
  
  const currentDirectory = useDocumentationStore.use.currentDirectory();
  const navigateToDirectory = useDocumentationStore.use.navigateToDirectory();
  
  const setStoreFilterModel = useDocumentationStore.use.setFilterModel();
  const setStoreDocumentState = useDocumentationStore.use.setDocumentState();
  const setStoreSortModel = useDocumentationStore.use.setSortModel();
  const setStoreSearchPhrase = useDocumentationStore.use.setSearchPhrase();
  const applyFilters = useDocumentationStore.use.applyFilters();
  const setSortValueGetters = useDocumentationStore.use.setSortValueGetters();
  const setGalleryFilesFromGridRows =
    useDocumentationStore.use.setGalleryFilesFromGridRows();

  const valueGetters = useValueGetters();

  /**
   * Rows visible in the grid, excluding optimistically removed rows.
   * This enables instant visual feedback on archive/move/DnD without scroll reset.
   */
  const visibleRows = useMemo(() => {
    if (removedRowIds.size === 0) {
      return filteredRows;
    }
    return filteredRows.filter((row) => !row._id || !removedRowIds.has(row._id));
  }, [filteredRows, removedRowIds]);

  const { filterModel: savedFilterModel, sortModel: initialSortModel } = useMemo(
    () => getInitialStateFromStorage(DataGridInstanceName.documentation),
    []
  );

  const initialFilterModel = useMemo(
    () => ({
      ...savedFilterModel,
      items: (savedFilterModel.items ?? []).filter(
        (item) => item.field !== GridColumnFieldDocumentation.state
      ),
    }),
    [savedFilterModel]
  );

  const [filterModel, setFilterModelLocal] = useState<GridFilterModel>(
    initialFilterModel
  );

  // No sort chosen means name ascending everywhere - show it in the header too.
  const [sortModel, setSortModelLocal] = useState<GridSortModel>(() =>
    resolveSortModel(initialSortModel)
  );

  // Seeds the documentation store with the grid filters and sorting saved in local storage.
  useEffect(() => {
    setStoreFilterModel(initialFilterModel);
    setStoreSortModel(initialSortModel);
  }, [
    initialFilterModel,
    initialSortModel,
    setStoreFilterModel,
    setStoreSortModel,
  ]);

  const setFilterModel = useCallback(
    (nextModel: GridFilterModel) => {
      setFilterModelLocal(nextModel);
      setStoreFilterModel(nextModel);
    },
    [setStoreFilterModel]
  );

  const setSortModel = useCallback(
    (model: GridSortModel) => {
      const effectiveSortModel = resolveSortModel(model);
      setSortModelLocal(effectiveSortModel);
      setStoreSortModel(effectiveSortModel);
    },
    [setStoreSortModel]
  );

  const setSearchPhrase = useCallback(
    (phrase: string) => {
      setStoreSearchPhrase(phrase);
    },
    [setStoreSearchPhrase]
  );

  const clearFiltersAndNavigate = useCallback(
    (state: DocumentState) => {
      const nextFilterModel: GridFilterModel = { items: [] };

      setStoreSearchPhrase('');

      setStoreDocumentState(state);
      setFilterModelLocal(nextFilterModel);
      setStoreFilterModel(nextFilterModel);

      setSortModelLocal(DEFAULT_SORT_MODEL);
      setStoreSortModel(DEFAULT_SORT_MODEL);
    },
    [
      setStoreDocumentState,
      setStoreSearchPhrase,
      setStoreFilterModel,
      setStoreSortModel,
    ]
  );

  const columns = useMemo(
    () => [...new DocumentationColumnsFactory(intl, timezone, organizationId).create()],
    [intl, timezone, organizationId]
  );

  // Sorting runs in the store (a sort change re-sorts locally), but the value
  // getters need React context - hand them over before filters are applied.
  useEffect(() => {
    setSortValueGetters(valueGetters);
  }, [valueGetters, setSortValueGetters]);

  useEffect(() => {
    applyFilters();
  }, [
    filterModel,
    searchPhrase,
    documentState,
    isLoadingSearch,
    // Note: currentDirectoryDirectories/currentDirectoryDocumentations intentionally NOT included.
    // Navigation uses fetchDirectoryContent which sets filteredRows directly.
    // Sort changes are handled by the store's setSortModel (local re-sort).
    applyFilters,
  ]);

  // Search results refreshed in the background (silent re-search after a
  // document changed) - rebuild the rows. Separate from the effect above:
  // clearing a search resets these arrays and must not trigger another
  // directory fetch.
  useEffect(() => {
    if (!isSearchActive({ searchPhrase })) {
      return;
    }
    applyFilters();
  }, [
    searchedDocumentations,
    searchedDirectories,
    searchPhrase,
    applyFilters,
  ]);

  // The gallery store shows the rows the grid shows.
  useEffect(() => {
    setGalleryFilesFromGridRows(visibleRows);
  }, [visibleRows, setGalleryFilesFromGridRows]);

  const isLoading = isLoadingCurrentDirDirectories || isLoadingCurrentDirDocumentations || isFiltering;

  return {
    rows: visibleRows,
    columns,
    filterModel,
    setFilterModel,
    sortModel,
    setSortModel,
    searchPhrase,
    setSearchPhrase,
    isLoading,
    isLoadingDirectories: isLoadingCurrentDirDirectories,
    isLoadingDocumentations: isLoadingCurrentDirDocumentations,
    isLoadingSearch,
    isFiltering,
    currentDirectory,
    navigateToDirectory,
    clearFiltersAndNavigate,
  };
}
